// AtlasLayersPanel — the atlas's layer switches (Story #6837 Stream 10b).
//
// Controlled and free of map logic: it says which layers are on, and the atlas
// decides what to draw. Six switches map to the six literal <g data-layer="…">
// wrappers in MyInventionsAtlas.tsx (footprints, documents, sectorRegions,
// markers, overlap, synergy). Two more rows are always off, each with the
// reason, so a layer the reader might expect to find says why it is not there
// rather than being silently absent.
//
// On the Sector lens the topology layers (holes / bridges / frontier markers,
// overlap, synergy) are not drawn, so their switches are disabled with the
// reason "Topology lens only", and the sector-regions switch appears. On any
// other lens the regions switch is not rendered at all.

import { COUNTRY_LABELS, MARKET_COUNTRIES } from '../../types/market'

export type AtlasLayerKey =
  | 'footprints'
  | 'documents'
  | 'sectorRegions'
  | 'markers'
  | 'overlap'
  | 'synergy'

export type AtlasLayerState = Record<AtlasLayerKey, boolean>

export const DEFAULT_ATLAS_LAYERS: AtlasLayerState = {
  footprints: true,
  documents: true,
  sectorRegions: true,
  markers: true,
  overlap: true,
  synergy: true,
}

export const TOPOLOGY_ONLY_REASON = 'Topology lens only'
export const CORPUS_UNDERLAY_REASON =
  'Not available for a portfolio view yet — nothing returns where the surrounding patents sit for a whole portfolio.'
export const SHARED_IN_REASON =
  'Shown in the Shared with you card; this map plots your own inventions.'

interface LayerRow {
  key: AtlasLayerKey
  label: string
  /** Only the Sector lens draws this layer. */
  sectorOnly?: boolean
  /** Only the Topology lens draws this layer. */
  topologyOnly?: boolean
}

const ROWS: LayerRow[] = [
  { key: 'footprints', label: 'Footprints' },
  { key: 'documents', label: 'Documents' },
  { key: 'sectorRegions', label: 'Sector regions', sectorOnly: true },
  { key: 'markers', label: 'Holes, bridges & frontier', topologyOnly: true },
  { key: 'overlap', label: 'Overlap', topologyOnly: true },
  { key: 'synergy', label: 'Synergy', topologyOnly: true },
]

const UNAVAILABLE_ROWS: Array<{ id: string; label: string; reason: string }> = [
  // Plain words on the screen: the UX-024 rule keeps the engineering term out of customer text.
  { id: 'corpus-underlay', label: 'Neighbouring patents', reason: CORPUS_UNDERLAY_REASON },
  { id: 'shared-in', label: 'Shared with you', reason: SHARED_IN_REASON },
]

/** A filter chip: on = the inventions it stands for are shown. Unticking hides them. */
export interface AtlasFilterChip {
  key: string
  label: string
  /** The full name when the label is shortened. */
  title?: string
  count: number
  on: boolean
}

/**
 * The Sector lens's filters (Stream 10c). Controlled, like the layers: the panel says what is
 * ticked and the atlas decides what to draw. It narrows what is DRAWN only; nothing here changes
 * which inventions the reader owns or the "not drawn" explanations.
 */
export interface AtlasFilters {
  sectors: AtlasFilterChip[]
  onToggleSector: (key: string) => void
  /** ISO3 for the sector value shown on each region's label. */
  country: string
  onCountry: (code: string) => void
  statuses: AtlasFilterChip[]
  onToggleStatus: (key: string) => void
  /** The footer: inventions drawn, plotted in all, and plotted but not yet classified. */
  shown: number
  total: number
  unclassified: number
  onReset: () => void
}

export interface AtlasLayersPanelProps {
  layers: AtlasLayerState
  onChange: (key: AtlasLayerKey, on: boolean) => void
  /** The atlas's current lens. Only 'sector' changes what this panel shows. */
  lens: string
  /** Present on the Sector lens only. */
  filters?: AtlasFilters
}

function Chip({ chip, onToggle, testId }: { chip: AtlasFilterChip; onToggle: () => void; testId: string }) {
  return (
    <button
      type="button"
      aria-pressed={chip.on}
      title={chip.title}
      data-testid={testId}
      onClick={onToggle}
      className={`rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${
        chip.on ? 'border-sky-400 bg-sky-50 text-iflow-700' : 'border-gray-300 bg-white text-gray-400 line-through'
      }`}
    >
      {chip.label} <span className="font-semibold">{chip.count}</span>
    </button>
  )
}

export default function AtlasLayersPanel({ layers, onChange, lens, filters }: AtlasLayersPanelProps) {
  const onSector = lens === 'sector'
  const hidden = filters ? filters.total - filters.shown : 0
  return (
    <div data-part="AtlasLayersPanel" role="group" aria-label="Atlas layers" className="space-y-1">
      <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Layers</h4>
      <ul className="space-y-1">
        {ROWS.filter((r) => !r.sectorOnly || onSector).map((r) => {
          const blocked = onSector && r.topologyOnly === true
          return (
            <li key={r.key}>
              <label
                className={`flex items-center gap-2 text-[12px] ${blocked ? 'text-gray-400' : 'text-gray-700'}`}
              >
                <input
                  type="checkbox"
                  data-testid={`atlas-layer-${r.key}`}
                  checked={!blocked && layers[r.key]}
                  disabled={blocked}
                  aria-describedby={blocked ? `atlas-layer-reason-${r.key}` : undefined}
                  onChange={(e) => {
                    // A disabled input cannot be clicked in a browser, but a synthetic
                    // event still reaches React; a layer the lens does not draw is never
                    // switched from here.
                    if (!blocked) onChange(r.key, e.target.checked)
                  }}
                />
                <span>{r.label}</span>
              </label>
              {blocked && (
                <p id={`atlas-layer-reason-${r.key}`} className="ml-6 text-[11px] text-gray-400">
                  {TOPOLOGY_ONLY_REASON}
                </p>
              )}
            </li>
          )
        })}
        {UNAVAILABLE_ROWS.map((r) => (
          <li key={r.id}>
            <label className="flex items-center gap-2 text-[12px] text-gray-400">
              <input
                type="checkbox"
                data-testid={`atlas-layer-${r.id}`}
                checked={false}
                disabled
                aria-describedby={`atlas-layer-reason-${r.id}`}
                readOnly
              />
              <span>{r.label}</span>
            </label>
            <p id={`atlas-layer-reason-${r.id}`} className="ml-6 text-[11px] text-gray-400">
              {r.reason}
            </p>
          </li>
        ))}
      </ul>

      {onSector && filters && (
        <div data-part="AtlasFilters" role="group" aria-label="Atlas filters" className="mt-3 space-y-2 border-t border-gray-200 pt-3">
          <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Filters</h4>
          {filters.sectors.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold text-gray-600">Sectors</p>
              <div className="flex flex-wrap gap-1">
                {filters.sectors.map((c) => (
                  <Chip key={c.key} chip={c} testId={`atlas-filter-sector-${c.key}`} onToggle={() => filters.onToggleSector(c.key)} />
                ))}
              </div>
            </div>
          )}
          <label className="block text-[11px] font-semibold text-gray-600">
            Sector value shown for
            <select
              data-testid="atlas-filter-country"
              value={filters.country}
              onChange={(e) => filters.onCountry(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-[12px] font-normal text-gray-800"
            >
              {MARKET_COUNTRIES.map((c) => (
                <option key={c} value={c}>{COUNTRY_LABELS[c]}</option>
              ))}
            </select>
          </label>
          <div>
            <p className="mb-1 text-[11px] font-semibold text-gray-600">Status</p>
            <div className="flex flex-wrap gap-1">
              {filters.statuses.map((c) => (
                <Chip key={c.key} chip={c} testId={`atlas-filter-status-${c.key}`} onToggle={() => filters.onToggleStatus(c.key)} />
              ))}
            </div>
          </div>
          <p data-testid="atlas-filter-footer" className="text-[11.5px] text-gray-600">
            showing {filters.shown} of {filters.total} · {hidden} hidden by filters · {filters.unclassified} not yet classified
            {hidden > 0 && (
              <>
                {' · '}
                <button type="button" data-testid="atlas-filter-reset" onClick={filters.onReset} className="font-semibold text-sky-700 underline">
                  reset filters
                </button>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}
